import {
  RiArrowLeftLine,
  RiArrowLeftSLine,
  RiArrowRightLine,
  RiArrowRightSLine,
} from '@remixicon/react';
import React, { useRef } from 'react';
import styled from 'styled-components';

export type TabItem = {
  key: number;
  label: string;
};

type ReportTabsProps = {
  tabItems: TabItem[];
  activeTab: number;
  onClickTab: (key: number) => void;
};

const scrollIncremental = 100;

const ReportTabs: React.FC<ReportTabsProps> = (props) => {
  const { activeTab, tabItems, onClickTab } = props;
  const tabsRef = useRef<HTMLDivElement | null>(null);

  const scrollTabs = (direction: string) => {
    if (tabsRef.current) {
      const { scrollLeft } = tabsRef.current;
      const scrollAmount =
        direction === 'left' ? -scrollIncremental : scrollIncremental;
      tabsRef.current.scrollTo({
        left: scrollLeft + scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const ensureTabVisible = (tabElement: HTMLButtonElement) => {
    if (tabsRef.current && tabElement) {
      const containerRect = tabsRef.current.getBoundingClientRect();
      const tabRect = tabElement.getBoundingClientRect();

      if (tabRect.left < containerRect.left) {
        tabsRef.current.scrollBy({
          left: tabRect.left - containerRect.left - (16 + 30),
          behavior: 'smooth',
        });
      } else if (tabRect.right > containerRect.right) {
        tabsRef.current.scrollBy({
          left: tabRect.right - containerRect.right + (16 + 30),
          behavior: 'smooth',
        });
      }
    }
  };

  return (
    <TabsContainer>
      <ScrollButtonLeft onClick={() => scrollTabs('left')}>
        <RiArrowLeftSLine size={20} />
      </ScrollButtonLeft>
      <ScrollButtonRight onClick={() => scrollTabs('right')}>
        <RiArrowRightSLine size={20} />
      </ScrollButtonRight>

      <TabsScrollArea ref={tabsRef}>
        {tabItems.map((tab) => (
          <TabButton
            active={activeTab === tab.key}
            key={tab.key}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              onClickTab(tab.key);
              ensureTabVisible(e.currentTarget);
            }}
          >
            {tab.label}
          </TabButton>
        ))}
      </TabsScrollArea>
    </TabsContainer>
  );
};

const TabsContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  border-bottom: 1px solid #ddd;

  @media (max-width: 767px) {
    background: #fff;
    border-bottom-color: #bfbfbf;
  }
`;

const ScrollButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: white;
  padding: 4px 4px;
  z-index: 10;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 768px) {
    /* display: none; */
  }

  @media (max-width: 767px) {
    display: none;
  }
`;

const ScrollButtonLeft = styled(ScrollButton)`
  left: 0;
`;

const ScrollButtonRight = styled(ScrollButton)`
  right: 0;
`;

const TabsScrollArea = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 8px;
  scroll-behavior: smooth;
  padding-left: 30px;
  padding-right: 30px;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (max-width: 767px) {
    gap: 0;
    padding-left: 0;
    padding-right: 0;
  }
`;

type TabButtonProps = {
  active: boolean;
};

const TabButton = styled.button<TabButtonProps>`
  white-space: nowrap;
  padding: 8px 16px;
  font-size: 14px;
  border-bottom: ${({ active }) =>
    active ? '3px solid var(--primary-1)' : 'none'};
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f5f5f5;
  }

  @media (max-width: 767px) {
    flex: 0 0 auto;
    padding: 10px;
    border-bottom: ${({ active }) =>
      active ? '2px solid #264284' : '2px solid transparent'};
    color: ${({ active }) => (active ? '#264284' : '#000')};
    font-size: 14px;
    font-weight: 400;
    line-height: 1.2;

    &:hover {
      background: #fff;
    }
  }
`;

export default ReportTabs;
